import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /**
   * Shows the dialog.
   *
   * @param {Event} [event] - The event that triggered the dialog, when opened declaratively.
   */
  showDialog(event) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Capture the trigger *synchronously*, before the requestAnimationFrame below.
    // Native `dialog.close()` restores focus to whatever was focused when
    // `showModal()` ran — but `showModal()` runs a frame later, and on macOS
    // clicking a <button> does not focus it, so the browser records <body> and
    // returns focus there. See #restoreFocus for the corresponding restore.
    this.#previouslyFocused = this.#resolveTrigger(event);

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    // Must run after `dialog.close()` so it wins over native focus restoration,
    // and before the close event so listeners can override it if they need to.
    this.#restoreFocus();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   *
   * @param {Event} [event] - The event that triggered the toggle, when toggled declaratively.
   */
  toggleDialog = (event) => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog(event);
    }
  };

  /**
   * Resolves the element that opened the dialog, so focus can return to it on close.
   *
   * @param {unknown} [event] - The event that triggered the dialog, if any.
   * @returns {HTMLElement | null} The trigger, or null when there is no meaningful one.
   */
  #resolveTrigger(event) {
    // `component.js` proxies `event.target` to the element carrying the `on:click`
    // attribute, so it identifies the trigger even when the click never moved focus.
    // Programmatic callers (quick-add, anchored-popover, the theme editor, CMD+K)
    // pass nothing, so fall back to whatever currently holds focus.
    const candidate = event instanceof Event ? event.target : document.activeElement;

    // <body> is the "focus was nowhere" sentinel — storing it would restore nothing.
    // #restoreFocus falls back to an aria-controls lookup in that case.
    if (!(candidate instanceof HTMLElement) || candidate === document.body) return null;

    return candidate;
  }

  /**
   * Returns focus to the element that opened the dialog.
   */
  #restoreFocus() {
    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;

    // A Section Rendering API morph between open and close can replace the trigger
    // node. The JS reference stays valid but the element is detached from the live
    // DOM — and .focus() on a detached node is a silent no-op, so we explicitly
    // check and fall back to a fresh query. Mirrors assets/theme-drawer.js.
    if (trigger && document.contains(trigger)) {
      trigger.focus();
      return;
    }

    if (!this.id) return;

    // CSS.escape guards against an id that would make this an invalid selector:
    // a SyntaxError here would abort closeDialog and swallow DialogCloseEvent.
    const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(this.id) : this.id;

    /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${escapedId}"]`))?.focus();
  }

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
